import type { LichessRatings, UserRatings } from '@/core/data/rating/rating.interface';
import type { RatingChessKey, RatingKey, RatingLichessKey } from '@/core/data/rating/rating.type';
import { RatingDisplayMode } from '@/core/data/user/user.type';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import { defineStore } from 'pinia';
import { ref } from 'vue';

const MIN_RATING = 150;

const LICHESS_KEYS: RatingLichessKey[] = [
  'lichessComClassicalRating',
  'lichessComRapidRating',
  'lichessComBlitzRating',
  'lichessComBulletRating',
];

const CHESS_COM_KEYS: RatingChessKey[] = [
  'chessComClassicalRating',
  'chessComRapidRating',
  'chessComBlitzRating',
  'chessComBulletRating',
];

export const useRatingStore = defineStore(GamStoreId.RATING, () => {
  const ratings = ref<UserRatings>({});
  const lichessRatings = ref<LichessRatings>({});
  const maxRating = ref(4000);
  const ratingDisplayMode = ref<RatingDisplayMode>(RatingDisplayMode.NUMBER);

  const isRatingAdded = (): boolean => {
    let ratingAdded = false;
    for (const key in ratings.value) {
      if (ratings.value[key as RatingKey] !== undefined) {
        const keyValue = ratings.value[key as RatingKey];
        if (keyValue && keyValue.toString().length && parseInt(keyValue.toString(), 10) <= maxRating.value) {
          ratingAdded = true;
          break;
        }
      }
    }
    return ratingAdded;
  };

  // Get from provider
  const isLichessConnected = (): boolean => {
    const r = lichessRatings.value;
    return !!(
      r.lichessComBlitzRating ||
      r.lichessComRapidRating ||
      r.lichessComBulletRating ||
      r.lichessComClassicalRating
    );
  };

  // Lichess omits ratings for time controls with too few played games. Those must stay
  // undefined so they are left out of the payload instead of being persisted as 0.
  const parseValue = (value?: number | string): number | undefined => {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    const parsed = parseInt(value.toString(), 10);
    return Number.isNaN(parsed) || parsed === 0 ? undefined : parsed;
  };

  const setLichessRatings = (r: LichessRatings) => {
    lichessRatings.value = {
      lichessComBlitzRating: parseValue(r.lichessComBlitzRating),
      lichessComRapidRating: parseValue(r.lichessComRapidRating),
      lichessComBulletRating: parseValue(r.lichessComBulletRating),
      lichessComClassicalRating: parseValue(r.lichessComClassicalRating),
      fideRating: parseValue(r.fideRating),
      title: r.title,
    };
    updateLichessRatings();
  };

  const updateLichessRatings = () => {
    ratings.value = {
      ...ratings.value,
      ...lichessRatings.value,
    };
  };

  const setOfflineRating = (rating?: number) => {
    ratings.value.rating = rating;
  };

  const setByRatingKey = (key: RatingKey, rating?: string | number) => {
    ratings.value[key] = rating ? parseInt(rating.toString(), 10) : undefined;
  };

  const deleteByRatingKey = (key: RatingKey) => {
    if (key in ratings.value) {
      delete ratings.value[key];
    }
  };

  const setRatingDisplayMode = (mode: RatingDisplayMode) => {
    ratingDisplayMode.value = mode;
    ratings.value = { ...ratings.value, ratingDisplayMode: mode };
  };

  const bestOf = (keys: (RatingLichessKey | RatingChessKey)[]): number | undefined => {
    const values = keys.map((key) => parseValue(ratings.value[key])).filter((value) => value !== undefined);
    return values.length ? Math.max(...values) : undefined;
  };

  // Mirrors the API's derivation order (FIDE, Lichess, Chess.com) so the IRL input can show
  // the value the server would resolve when no rating was typed.
  const getBestAvailableRating = (): number | undefined => {
    const derived = parseValue(ratings.value.fideRating) ?? bestOf(LICHESS_KEYS) ?? bestOf(CHESS_COM_KEYS);
    return derived === undefined ? undefined : Math.max(derived, MIN_RATING);
  };

  const typedRating = (): number | undefined => {
    const val = ratings.value.rating;
    return typeof val === 'number' && Number.isFinite(val) && val > 0 ? Math.max(val, MIN_RATING) : undefined;
  };

  // Only keeps a value the user actually typed. Anything else — untyped, cleared, or the NaN
  // an emptied number input emits — is dropped so the API derives the rating instead.
  const clampGambitsRating = () => {
    const typed = typedRating();
    if (typed === undefined) {
      delete ratings.value.rating;
      return;
    }
    ratings.value.rating = typed;
  };

  // The IRL rating is required, so the input must never be left in an invalid state. On blur
  // an emptied value falls back to whatever the API would have derived.
  const resolveGambitsRating = () => {
    ratings.value.rating = typedRating() ?? getBestAvailableRating();
  };

  const reset = () => {
    ratings.value = {};
    ratingDisplayMode.value = RatingDisplayMode.NUMBER;
  };

  return {
    ratings,
    maxRating,
    ratingDisplayMode,
    setLichessRatings,
    setOfflineRating,
    setByRatingKey,
    deleteByRatingKey,
    isRatingAdded,
    isLichessConnected,
    updateLichessRatings,
    setRatingDisplayMode,
    clampGambitsRating,
    resolveGambitsRating,
    getBestAvailableRating,
    reset,
  };
});
